/**
 * 自由画布首页（P1 C-2a）。
 *
 * 定位：全站总线的门面。用户从这里进入某张画布编辑（@xyflow 编辑器在 C-2b 接入）、
 * 打开官方教程画布、或直达 3D 导演台。
 *
 * 视觉：原创设计（我方"石墨暖底+琥珀"体系），未照搬任何竞品画面；背景是
 * DotMatrixBackground（鼠标近邻发光的点阵，纯装饰、性能安全、可降级）。
 * D-3 闸门：导演台入口永远可点，能否进入由能力闸门决定。
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, Compass, Plus, Sparkles, Trash2 } from 'lucide-react';

import { DirectorGateDialog } from '../../features/director/DirectorGateDialog';
import { createTutorialCanvas } from '../../features/canvas/tutorialCanvas';
import {
  deleteCanvas,
  listCanvases,
  loadCanvas,
  newCanvasId,
  type CanvasMeta,
} from '../../features/canvas/canvasLibrary';
import { useCanvasStore } from '../../features/canvas/canvasStore';
import { createEmptyCanvas } from '../../model/canvas';
import { DotMatrixBackground } from '../../ui/DotMatrixBackground';
import { useAppCapability } from '../capabilityContext';

/** 相对时间：刚刚 / N 分钟前 / N 小时前 / N 天前 / M-D（首页展示用） */
function formatWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) {
    return '';
  }
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) {
    return '刚刚';
  }
  if (min < 60) {
    return `${min} 分钟前`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr} 小时前`;
  }
  const day = Math.floor(hr / 24);
  if (day < 30) {
    return `${day} 天前`;
  }
  const d = new Date(t);
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

/** 教程画布的迷你节点流预览（纯静态装饰，讲清"想法→成片"的连线感） */
const FLOW_STEPS = ['想法', '角色', '导演台', '关键帧', '视频', '成片'];

function FlowPreview() {
  return (
    <div className="flex items-center gap-1 overflow-hidden">
      {FLOW_STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-1">
          <span className="whitespace-nowrap rounded-control border border-surface-edge bg-surface-base px-1.5 py-1 text-uiSm text-ink-secondary">
            {s}
          </span>
          {i < FLOW_STEPS.length - 1 && (
            <span className="h-px w-3 shrink-0 bg-accent/50" aria-hidden="true" />
          )}
        </div>
      ))}
    </div>
  );
}

export function CanvasPage() {
  const { gate } = useAppCapability();
  const navigate = useNavigate();
  const [blocked, setBlocked] = useState(false);
  const [canvases, setCanvases] = useState<CanvasMeta[]>(() => listCanvases());
  // 删除二次确认：首点置为待确认（项目禁用 window.confirm），再点才真删
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  // 从编辑器返回时本页重挂→重读列表；删除后也靠 setCanvases 刷新
  useEffect(() => {
    setCanvases(listCanvases());
  }, []);

  function tryEnterDirector(): void {
    if (gate === null) {
      return;
    }
    if (gate.action === 'enter') {
      navigate('/director');
      return;
    }
    setBlocked(true);
  }

  function openTutorial(): void {
    useCanvasStore.getState().setDoc(createTutorialCanvas());
    navigate('/canvas/editor');
  }

  function openNewCanvas(): void {
    useCanvasStore.getState().setDoc(createEmptyCanvas(newCanvasId(), new Date().toISOString()));
    navigate('/canvas/editor');
  }

  function openCanvas(id: string): void {
    const next = loadCanvas(id);
    if (next === null) {
      setCanvases(listCanvases()); // 该画布已坏/丢失：刷新列表剔除
      return;
    }
    useCanvasStore.getState().setDoc(next);
    navigate('/canvas/editor');
  }

  function removeCanvas(e: { stopPropagation(): void }, id: string): void {
    e.stopPropagation();
    deleteCanvas(id);
    setCanvases(listCanvases());
    setPendingDelete(null);
  }

  function armDelete(e: { stopPropagation(): void }, id: string): void {
    e.stopPropagation();
    setPendingDelete((cur) => (cur === id ? null : id));
  }

  return (
    <div className="relative h-full overflow-y-auto">
      <DotMatrixBackground className="opacity-70" />

      <div className="relative mx-auto flex min-h-full max-w-5xl flex-col px-6 py-10">
        {/* Hero */}
        <header className="mb-8">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-surface-edge bg-surface-overlay/70 px-2.5 py-1 text-uiSm text-accent">
            <Sparkles size={13} />
            节点式创作总线
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">自由画布</h1>
          <p className="mt-1 max-w-xl text-ui text-ink-muted">
            把想法、角色、镜头、生成与剪辑连成一张网——每个节点是一步创作，连线是数据的流动。
          </p>
        </header>

        {/* 主卡片 */}
        <section className="grid gap-4 sm:grid-cols-2">
          {/* 官方教程画布 */}
          <article className="panel flex flex-col gap-3 p-5">
            <div className="flex items-center gap-2">
              <Compass size={18} className="text-accent" />
              <h2 className="text-ui font-semibold">官方教程画布</h2>
            </div>
            <p className="text-uiSm text-ink-muted">
              一张预置示例，演示"从一个想法到一条成片"如何跨节点流转。打开后可自由改动，不影响源。
            </p>
            <FlowPreview />
            <button
              type="button"
              className="btn-primary mt-1 self-start"
              onClick={openTutorial}
            >
              打开教程画布
            </button>
          </article>

          {/* 3D 导演台 */}
          <article className="panel flex flex-col gap-3 p-5">
            <div className="flex items-center gap-2">
              <Boxes size={18} className="text-accent" />
              <h2 className="text-ui font-semibold">3D 导演台</h2>
            </div>
            <p className="text-uiSm text-ink-muted">
              在 3D 空间摆放站位与姿势、逐关节微调、切机位连拍，截图落成画布节点供后续生成使用。
            </p>
            <button type="button" className="btn-ghost mt-1 self-start" onClick={tryEnterDirector}>
              进入 3D 导演台
            </button>
          </article>
        </section>

        {/* 我的画布 */}
        <section className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-ui font-semibold">我的画布</h2>
            <button
              type="button"
              className="btn-ghost inline-flex items-center gap-1.5"
              onClick={openNewCanvas}
            >
              <Plus size={15} />
              新建画布
            </button>
          </div>
          {canvases.length === 0 ? (
            <div className="panel flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
              <p className="text-ui text-ink-secondary">还没有画布</p>
              <p className="max-w-sm text-uiSm text-ink-muted">
                点「新建画布」开始创作，或打开官方教程画布了解结构。你的每张画布都会自动保存在此。
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {canvases.map((c) => (
                <article
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  className="panel group relative flex cursor-pointer flex-col gap-2 p-4"
                  onClick={() => openCanvas(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      openCanvas(c.id);
                    }
                  }}
                >
                  {c.cover !== undefined && (
                    <div className="h-20 overflow-hidden rounded-control border border-surface-line">
                      <img src={c.cover} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                  <h3 className="truncate pr-7 text-ui font-semibold">{c.title}</h3>
                  <div className="flex items-center gap-1.5 text-uiSm text-ink-muted">
                    <span className="font-num font-semibold text-accent">{c.nodeCount}</span>
                    <span>节点</span>
                    <span className="text-ink-faint">·</span>
                    <span>{formatWhen(c.updatedAt)}</span>
                  </div>
                  <button
                    type="button"
                    aria-label="删除画布"
                    title={pendingDelete === c.id ? '再次点击确认删除' : '删除画布'}
                    className={
                      pendingDelete === c.id
                        ? 'absolute right-2 top-2 rounded-control bg-state-danger/15 px-2 py-1 text-uiSm text-state-danger opacity-100'
                        : 'absolute right-2 top-2 rounded-control p-1.5 text-ink-faint opacity-0 transition-opacity hover:bg-surface-raised hover:text-state-danger focus:opacity-100 group-hover:opacity-100'
                    }
                    onClick={(e) => (pendingDelete === c.id ? removeCanvas(e, c.id) : armDelete(e, c.id))}
                  >
                    {pendingDelete === c.id ? '确认删除？' : <Trash2 size={15} />}
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <DirectorGateDialog open={blocked} onClose={() => setBlocked(false)} />
    </div>
  );
}
